import React, { useState } from 'react';
import { Timer, Activity, Play, Info } from 'lucide-react';
import { authenticatedFetchJSON } from '../../utils/authenticatedFetch';

type AltModelType = 'arrhenius' | 'coffin-manson' | 'peck' | 'blacks' | 'eyring';

const AltAnalysis: React.FC = () => {
    const [model, setModel] = useState<AltModelType>('arrhenius');
    const [params, setParams] = useState<{ [key: string]: string | number }>({});
    const [result, setResult] = useState<any | null>(null);
    const [loading, setLoading] = useState(false);

    const modelOptions: { value: AltModelType, label: string }[] = [
        { value: 'arrhenius', label: 'Arrhenius (Temperature)' },
        { value: 'coffin-manson', label: 'Coffin-Manson (Thermal Cycle)' },
        { value: 'peck', label: 'Hallberg-Peck (Temp + Humidity)' },
        { value: 'blacks', label: 'Black\'s Equation (Electromigration)' },
        { value: 'eyring', label: 'Eyring (Generalized Stress)' },
    ];

    const getFields = (type: AltModelType) => {
        switch (type) {
            case 'arrhenius': return [
                { key: 'temp_use', label: 'Use Temp (°C)', type: 'number', toolTip: 'Normal operating temperature' },
                { key: 'temp_stress', label: 'Stress Temp (°C)', type: 'number', toolTip: 'Accelerated test temperature' },
                { key: 'ea', label: 'Activation Energy (eV)', type: 'number', placeholder: '0.7' }
            ];
            case 'coffin-manson': return [
                { key: 'delta_temp_use', label: 'Use ΔT (°C)', type: 'number' },
                { key: 'delta_temp_stress', label: 'Test ΔT (°C)', type: 'number' },
                { key: 'm_coeff', label: 'Coffin-Manson Exponent', type: 'number', placeholder: '2.5' }
            ];
            case 'peck': return [
                { key: 'temp_use', label: 'Use Temp (°C)', type: 'number' },
                { key: 'temp_stress', label: 'Stress Temp (°C)', type: 'number' },
                { key: 'rh_use', label: 'Use Humidity (%)', type: 'number' },
                { key: 'rh_stress', label: 'Stress Humidity (%)', type: 'number' },
                { key: 'ea', label: 'Activation Energy (eV)', type: 'number', placeholder: '0.7' }
            ];
            case 'blacks': return [
                { key: 'j_use', label: 'Current Density Use (A/cm²)', type: 'number' },
                { key: 'j_stress', label: 'Current Density Stress (A/cm²)', type: 'number' },
                { key: 'temp_use', label: 'Use Temp (°C)', type: 'number' },
                { key: 'temp_stress', label: 'Stress Temp (°C)', type: 'number' },
                { key: 'n', label: 'Current Exponent (n)', type: 'number', placeholder: '2.0' }
            ];
            case 'eyring': return [
                { key: 'temp_use', label: 'Use Temp (°C)', type: 'number' },
                { key: 'temp_stress', label: 'Stress Temp (°C)', type: 'number' },
                { key: 'stress_use', label: 'Use Stress (V)', type: 'number' },
                { key: 'stress_test', label: 'Test Stress (V)', type: 'number' },
                { key: 'ea_ev', label: 'Activation Energy (eV)', type: 'number', placeholder: '0.7' }
            ];
            default: return [];
        }
    };

    const handleCalculate = async () => {
        setLoading(true);
        setResult(null);
        try {
            const payload = {
                model: model,
                parameters: Object.entries(params).reduce((acc, [k, v]) => ({
                    ...acc,
                    [k]: typeof v === 'string' && !isNaN(parseFloat(v)) ? parseFloat(v) : v
                }), {})
            };
            const data = await authenticatedFetchJSON('/api/v1/lab/calculate-acceleration', {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            setResult(data);
        } catch (error) {
            console.error(error);
            setResult({ error: "Calculation failed" });
        } finally {
            setLoading(false);
        }
    };

    const currentFields = getFields(model);

    return (
        <div className="bg-card border border-border p-6 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Timer className="text-orange-500" /> Accelerated Life Testing (ALT)
                </h2>
                <select
                    value={model}
                    onChange={(e) => {
                        setModel(e.target.value as AltModelType);
                        setParams({});
                        setResult(null);
                    }}
                    className="bg-background border border-border rounded px-2 py-1 text-sm outline-none focus:border-primary"
                >
                    {modelOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                    {currentFields.map(field => (
                        <div key={field.key}>
                            <div className="flex items-center gap-1 mb-1">
                                <label className="block text-xs font-medium text-text-secondary">{field.label}</label>
                                {field.toolTip && <span title={field.toolTip}><Info size={12} className="text-text-secondary cursor-help" /></span>}
                            </div>
                            <input
                                type="number"
                                className="w-full bg-background border border-border rounded p-2 text-sm focus:ring-1 focus:ring-primary"
                                value={params[field.key] || ''}
                                onChange={e => setParams(prev => ({ ...prev, [field.key]: e.target.value }))}
                                placeholder={field.placeholder?.toString()}
                            />
                        </div>
                    ))}

                    <button
                        onClick={handleCalculate}
                        disabled={loading}
                        className="w-full bg-primary text-white py-2 rounded flex justify-center items-center gap-2 hover:bg-primary/90 mt-4 disabled:opacity-50"
                    >
                        {loading ? <Activity className="animate-spin" /> : <Play size={16} />}
                        Calculate Acceleration Factor
                    </button>
                </div>

                <div className="bg-muted/20 border border-border rounded p-4 h-full min-h-[200px]">
                    <h3 className="text-sm font-semibold text-text-secondary mb-2 uppercase tracking-wide">Results</h3>
                    {result ? (
                        <div className="space-y-4">
                            {result.acceleration_factor && (
                                <div className="text-center p-4 bg-background rounded border border-border">
                                    <div className="text-xs text-text-secondary uppercase">Acceleration Factor (AF)</div>
                                    <div className="text-3xl font-bold text-primary mt-1">{result.acceleration_factor.toFixed(2)}x</div>
                                </div>
                            )}

                            {result.interpretation && (
                                <div className="text-sm italic text-text-secondary border-l-2 border-primary/50 pl-3">
                                    &quot;{result.interpretation}&quot;
                                </div>
                            )}

                            <div className="space-y-1 mt-4">
                                {Object.entries(result).map(([k, v]) => {
                                    if (k === 'acceleration_factor' || k === 'interpretation' || k === 'model') return null;
                                    return (
                                        <div key={k} className="flex justify-between border-b border-border/50 py-1 last:border-0">
                                            <span className="text-xs text-text-secondary capitalize">{k.replace(/_/g, ' ')}</span>
                                            <span className="text-sm font-mono text-text-primary text-right">{String(v)}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-text-secondary opacity-50">
                            <Timer size={32} className="mb-2" />
                            <p className="text-xs">Input stress parameters to calculate time compression.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AltAnalysis;
